/**
 * SchoolSlugRedirect — public route /s/:slug that resolves a vanity slug
 * to a tenant_id and redirects to the school's public landing.
 * Supports ?redirect= query param for deep-linking (e.g., QR codes to admissions form).
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SchoolSlugRedirect() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/school/by-slug/${encodeURIComponent(slug)}`);
        if (!r.ok) {
          if (!cancelled) setError('School not found');
          return;
        }
        const data = await r.json();
        if (!cancelled && data?.tenant_id) {
          // Check for redirect query param (used by QR codes for deep-linking)
          const redirectPath = searchParams.get('redirect');
          if (redirectPath && redirectPath.startsWith('/')) {
            // Honor the redirect path if it's a valid relative path
            navigate(redirectPath, { replace: true });
          } else {
            // Default: redirect to school's public website page
            navigate(`/school/${data.tenant_id}/website`, { replace: true });
          }
        }
      } catch {
        if (!cancelled) setError('Failed to resolve URL');
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate, searchParams]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-3" data-testid="school-slug-redirect">
      {error ? (
        <>
          <p className="text-slate-600">{error}</p>
          <button className="text-sm text-indigo-600 underline" onClick={() => navigate('/')}>Back to home</button>
        </>
      ) : (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          <p className="text-sm text-slate-500">Resolving <code className="text-xs">/s/{slug}</code>…</p>
        </>
      )}
    </div>
  );
}
